import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, ChevronLeft, ChevronRight, Package, ShoppingCart, X, Zap } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Store = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const { addToCart } = useCart();

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory, selectedSubcategory, searchQuery, currentPage]);

  useEffect(() => {
    if (selectedCategory) {
      fetchSubcategories(selectedCategory);
    } else {
      setSubcategories([]);
      setSelectedSubcategory('');
    }
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/store/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchSubcategories = async (categoryId) => {
    try {
      const response = await axios.get(`${API_URL}/store/subcategories?category_id=${categoryId}`);
      setSubcategories(response.data);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/store/products?page=${currentPage}&limit=12`;
      if (selectedCategory) url += `&category_id=${selectedCategory}`;
      if (selectedSubcategory) url += `&subcategory_id=${selectedSubcategory}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      
      const response = await axios.get(url);
      setProducts(response.data.products);
      setTotalPages(response.data.pages);
      setTotalProducts(response.data.total);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
    toast.success(`${product.name} added to cart!`);
  };

  const handleBuyNow = (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
    navigate('/cart');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchProducts();
  };

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId === selectedCategory ? '' : categoryId);
    setSelectedSubcategory('');
    setCurrentPage(1);
  };

  const handleSubcategoryClick = (subcategoryId) => {
    setSelectedSubcategory(subcategoryId === selectedSubcategory ? '' : subcategoryId);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSelectedCategory('');
    setSelectedSubcategory('');
    setSearchQuery('');
    setCurrentPage(1);
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || '';
  };

  const getSelectedCategoryName = () => {
    const cat = categories.find(c => c.id === selectedCategory);
    return cat?.name || 'All Products';
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <SEO 
        title="Store | UAV Geo Solutions - Drone Parts, Robotics Kits & Electronics"
        description="Shop drone parts, robotics kits, IoT devices, batteries, and more. Quality products for drone enthusiasts and professionals."
        url="/store"
      />
      
      {/* Hero Banner */}
      <div className="relative pt-24 pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-[120px]" />
        
        <div className="max-w-7xl mx-auto px-4 md:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="inline-block px-4 py-1.5 bg-primary/10 border border-primary/20 rounded-full text-primary text-sm font-medium mb-4">
              Premium Tech Store
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 tracking-tight">
              Explore Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-cyan-400">Collection</span>
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-8">
              Quality drone parts, robotics kits, and electronics for enthusiasts and professionals
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
                <input
                  type="text"
                  placeholder="Search for drones, robotics kits, electronics..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-32 py-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20 text-white placeholder-gray-500 transition-all"
                  data-testid="store-search-input"
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-6 py-2.5 bg-primary hover:bg-primary/90 text-dark font-semibold rounded-xl transition-colors"
                >
                  Search
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 pb-24">
        {/* Category Pills */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Browse Categories</h2>
            {(selectedCategory || searchQuery) && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1.5 text-sm text-red-400 hover:text-red-300 transition-colors"
              >
                <X size={14} />
                Clear All
              </button>
            )}
          </div>
          
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => { setSelectedCategory(''); setSelectedSubcategory(''); setCurrentPage(1); }}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                !selectedCategory 
                  ? 'bg-primary text-dark shadow-lg shadow-primary/25' 
                  : 'bg-white/5 text-gray-300 hover:bg-white/10 border border-white/10 hover:border-white/20'
              }`}
            >
              All Products
            </button>
            {categories.map((category, index) => {
              const isSelected = selectedCategory === category.id;
              return (
                <motion.button
                  key={category.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.03 }}
                  onClick={() => handleCategoryClick(category.id)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                    isSelected 
                      ? 'bg-primary text-dark shadow-lg shadow-primary/25' 
                      : 'bg-white/5 text-gray-300 hover:bg-white/10 border border-white/10 hover:border-white/20'
                  }`}
                  data-testid={`category-${category.slug}`}
                >
                  {category.name}
                </motion.button>
              );
            })}
          </div>
        </motion.div>

        {/* Subcategories */}
        <AnimatePresence>
          {selectedCategory && subcategories.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-8 overflow-hidden"
            >
              <div className="flex flex-wrap gap-2 pl-4 border-l-2 border-primary/30">
                {subcategories.map((sub, index) => (
                  <motion.button
                    key={sub.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.03 }}
                    onClick={() => handleSubcategoryClick(sub.id)}
                    className={`px-4 py-2 rounded-lg text-sm transition-all duration-300 ${
                      selectedSubcategory === sub.id
                        ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                        : 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 border border-transparent'
                    }`}
                    data-testid={`subcategory-${sub.slug}`}
                  >
                    {sub.name}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Results Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
              {getSelectedCategoryName()}
              {selectedSubcategory && subcategories.find(s => s.id === selectedSubcategory) && (
                <span className="text-primary"> / {subcategories.find(s => s.id === selectedSubcategory)?.name}</span>
              )}
            </h2>
            <p className="text-gray-500 text-sm mt-1" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
              Showing {products.length} of {totalProducts} products
            </p>
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white/5 rounded-2xl overflow-hidden animate-pulse">
                <div className="h-56 bg-white/10" />
                <div className="p-5 space-y-3">
                  <div className="h-3 bg-white/10 rounded w-1/3" />
                  <div className="h-5 bg-white/10 rounded w-3/4" />
                  <div className="h-3 bg-white/5 rounded w-full" />
                  <div className="h-6 bg-white/10 rounded w-1/3 mt-4" />
                </div>
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-20 bg-white/5 rounded-3xl border border-white/10"
          >
            <div className="w-20 h-20 mx-auto mb-6 bg-white/5 rounded-full flex items-center justify-center">
              <Package size={40} className="text-gray-600" />
            </div>
            <h3 className="text-2xl font-semibold text-white mb-2">No products found</h3>
            <p className="text-gray-500 mb-8 max-w-md mx-auto">
              We couldn't find any products matching your criteria. Try adjusting your filters or search terms.
            </p>
            <button
              onClick={clearFilters}
              className="px-8 py-3 bg-primary text-dark font-semibold rounded-xl hover:bg-primary/90 transition-colors"
            >
              View All Products
            </button>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className="group"
              >
                <div
                  className="bg-[#12121a] rounded-2xl overflow-hidden border border-white/10 hover:border-primary/40 transition-all duration-300 h-full flex flex-col"
                  data-testid={`product-card-${product.id}`}
                >
                  {/* Product Image */}
                  <Link to={`/store/product/${product.slug || product.id}`} className="block relative">
                    <div className="relative h-52 bg-gradient-to-br from-white/5 to-transparent overflow-hidden">
                      {product.images?.[0] ? (
                        <img
                          src={product.images[0]}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-white/5">
                          <Package size={48} className="text-gray-600" />
                        </div>
                      )}
                      
                      {/* Discount Badge */}
                      {product.discount_price && (
                        <span className="absolute top-3 left-3 px-2.5 py-1 bg-red-500 text-white text-xs font-bold rounded-lg">
                          -{Math.round((1 - product.discount_price / product.price) * 100)}%
                        </span>
                      )}
                      
                      {/* Low Stock Badge */}
                      {product.stock_quantity <= 5 && product.stock_quantity > 0 && (
                        <span className="absolute top-3 right-3 px-2.5 py-1 bg-orange-500 text-white text-xs font-bold rounded-lg">
                          Only {product.stock_quantity} left
                        </span>
                      )}
                    </div>
                  </Link>

                  {/* Product Info */}
                  <div className="p-4 flex flex-col flex-grow">
                    {/* Category Tag */}
                    <span className="inline-block w-fit px-2 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded mb-2">
                      {getCategoryName(product.category_id)}
                    </span>
                    
                    {/* Title */}
                    <Link to={`/store/product/${product.slug || product.id}`}>
                      <h3 className="font-medium text-white text-base mb-1 line-clamp-2 hover:text-primary transition-colors" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
                        {product.name}
                      </h3>
                    </Link>
                    
                    {/* Description */}
                    <p className="text-gray-400 text-sm mb-3 line-clamp-2" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
                      {product.short_description}
                    </p>
                    
                    {/* Price */}
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-xl font-bold text-white" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
                        ₹{(product.discount_price || product.price).toLocaleString()}
                      </span>
                      {product.discount_price && (
                        <span className="text-sm text-gray-500 line-through">
                          ₹{product.price.toLocaleString()}
                        </span>
                      )}
                    </div>

                    {/* Stock Status */}
                    {product.stock_quantity > 0 ? (
                      <p className="text-xs text-green-400 mb-4 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-green-400 rounded-full" />
                        In Stock
                      </p>
                    ) : (
                      <p className="text-xs text-red-400 mb-4 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-red-400 rounded-full" />
                        Out of Stock
                      </p>
                    )}

                    {/* Action Buttons - Always Visible */}
                    <div className="mt-auto flex gap-2">
                      <button
                        onClick={(e) => handleAddToCart(product, e)}
                        disabled={product.stock_quantity === 0}
                        className="flex-1 py-2.5 bg-white/10 hover:bg-white/20 text-white text-sm font-medium rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-white/10"
                        data-testid={`add-to-cart-${product.id}`}
                      >
                        <ShoppingCart size={16} />
                        Add to Cart
                      </button>
                      <button
                        onClick={(e) => handleBuyNow(product, e)}
                        disabled={product.stock_quantity === 0}
                        className="flex-1 py-2.5 bg-primary hover:bg-primary/90 text-dark text-sm font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid={`buy-now-${product.id}`}
                      >
                        <Zap size={16} />
                        Buy Now
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-16">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="p-3 bg-white/5 rounded-xl border border-white/10 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/10 hover:border-white/20 transition-all"
            >
              <ChevronLeft size={20} className="text-gray-400" />
            </button>
            
            <div className="flex items-center gap-1">
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`min-w-[44px] h-11 rounded-xl text-sm font-medium transition-all ${
                    currentPage === i + 1
                      ? 'bg-primary text-dark shadow-lg shadow-primary/25'
                      : 'bg-white/5 text-gray-400 hover:bg-white/10 border border-white/10'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>

            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="p-3 bg-white/5 rounded-xl border border-white/10 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/10 hover:border-white/20 transition-all"
            >
              <ChevronRight size={20} className="text-gray-400" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Store;
